import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from './constants';

export function usePrintExport({ API, getAuthHeader, fetchData, allDocuments, calculateMontantTTC, getAllAcomptesESS, calculatedComptaStats, selectedDocument, setSelectedDocument, filterDateDebut, filterDateFin, setGeneratingRecu, setExportLoading, assignForm, setAssignForm, setShowAssignModal, shareForm, setShareForm, setShowShareModal, setSharing }) {

  // ============================================
  // IMPRESSION ÉCHÉANCIER PROFESSIONNEL
  // ============================================

  // Imprimer l'échéancier complet d'un document
  const printEcheancier = async (doc) => {
    setGeneratingRecu(true);
    try {
      const refContribution = doc.numero_police || doc.id;
      const echeances = doc.echeancier || [];
      
      try {
        const response = await axios.post(
          `${API}/souscriptions/admin/${doc.id}/print-echeancier`,
          {
            reference_contribution: refContribution,
            raison_sociale: doc.raison_sociale,
            siret: doc.siret,
            montant_total: calculateMontantTTC(doc),
            echeances: echeances.map((e, idx) => ({
              numero: idx + 1,
              date_prevue: e.date_prevue,
              montant: e.montant,
              statut: e.statut || 'en_attente',
              date_paiement: e.date_paiement || null
            }))
          },
          { 
            headers: getAuthHeader(),
            responseType: 'blob'
          }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `Echeancier_${refContribution}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Échéancier ${refContribution} téléchargé`);
      } catch (apiError) {
        if (apiError.response?.status === 404) {
          generateEcheancierHTML(doc, echeances, refContribution);
        } else {
          throw apiError;
        }
      }
    } catch {
      toast.error('Erreur lors de l\'impression de l\'échéancier');
    } finally {
      setGeneratingRecu(false);
    }
  };

  // Générer un échéancier HTML pour impression (fallback)
  const generateEcheancierHTML = (doc, echeances, refContribution) => {
    const montantTotal = calculateMontantTTC(doc);
    const montantPaye = echeances.filter(e => e.statut === 'paye').reduce((s, e) => s + (e.montant || 0), 0);
    const montantRestant = montantTotal - montantPaye;
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Échéancier - ${refContribution}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; }
          .header { text-align: center; border-bottom: 3px solid #10b981; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { color: #10b981; margin: 0; font-size: 24px; }
          .header h2 { color: #374151; margin: 10px 0 0 0; font-size: 18px; }
          .info-box { background: #f3f4f6; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
          .info-row { display: flex; justify-content: space-between; margin: 8px 0; }
          .info-label { color: #6b7280; }
          .info-value { font-weight: bold; color: #111827; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background: #10b981; color: white; padding: 12px; text-align: left; }
          td { padding: 12px; border-bottom: 1px solid #e5e7eb; }
          tr:nth-child(even) { background: #f9fafb; }
          .status-paye { color: #10b981; font-weight: bold; }
          .status-attente { color: #f59e0b; font-weight: bold; }
          .totals { background: #ecfdf5; padding: 20px; border-radius: 8px; margin-top: 30px; }
          .total-row { display: flex; justify-content: space-between; margin: 10px 0; font-size: 16px; }
          .total-main { font-size: 20px; font-weight: bold; color: #10b981; }
          .footer { text-align: center; margin-top: 40px; color: #9ca3af; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>ECHEANCIER DE PAIEMENT</h1>
          <h2>Contribution Solidaire ESS</h2>
        </div>
        
        <div class="info-box">
          <div class="info-row">
            <span class="info-label">Référence :</span>
            <span class="info-value">${refContribution}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Entreprise :</span>
            <span class="info-value">${doc.raison_sociale || 'N/A'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">SIRET :</span>
            <span class="info-value">${doc.siret || 'N/A'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Date d'édition :</span>
            <span class="info-value">${new Date().toLocaleDateString('fr-FR')}</span>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>N°</th>
              <th>Date prévue</th>
              <th>Montant</th>
              <th>Statut</th>
              <th>Date paiement</th>
            </tr>
          </thead>
          <tbody>
            ${echeances.map((e, idx) => `
              <tr>
                <td><strong>${idx + 1}</strong></td>
                <td>${e.date_prevue ? new Date(e.date_prevue).toLocaleDateString('fr-FR') : '-'}</td>
                <td><strong>${(e.montant || 0).toLocaleString('fr-FR')} &euro;</strong></td>
                <td class="${e.statut === 'paye' ? 'status-paye' : 'status-attente'}">
                  ${e.statut === 'paye' ? 'Payé' : 'En attente'}
                </td>
                <td>${e.date_paiement ? new Date(e.date_paiement).toLocaleDateString('fr-FR') : '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <div class="total-row">
            <span>Montant total Global :</span>
            <span class="total-main">${montantTotal.toLocaleString('fr-FR')} &euro;</span>
          </div>
          <div class="total-row">
            <span>Déjà payé :</span>
            <span style="color: #10b981; font-weight: bold;">${montantPaye.toLocaleString('fr-FR')} &euro;</span>
          </div>
          <div class="total-row">
            <span>Reste à payer :</span>
            <span style="color: #dc2626; font-weight: bold;">${montantRestant.toLocaleString('fr-FR')} &euro;</span>
          </div>
        </div>
        
        <div class="footer">
          <p>Document généré le ${new Date().toLocaleString('fr-FR')}</p>
          <p>Référence contribution solidaire : ${refContribution}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = function() {
      printWindow.print();
    };
    
    toast.success('Échéancier prêt pour impression');
  };

  // Supprimer l'échéancier d'une souscription ESS
  const handleDeleteEcheancier = async (doc) => {
    if (!doc?.id) {
      toast.error('ID de souscription manquant');
      return;
    }

    const echeances = doc?.echeancier || [];
    const paidCount = echeances.filter(e => e.paye || e.statut === 'paye').length;

    const confirmMessage = paidCount > 0 
      ? `Attention: ${paidCount} échéance(s) déjà payée(s). Voulez-vous vraiment supprimer cet échéancier ?`
      : 'Voulez-vous vraiment supprimer cet échéancier ?';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/souscriptions/admin/${doc.id}/echeancier`,
        { headers: getAuthHeader() }
      );

      toast.success(`Échéancier supprimé (${echeances.length} échéances)`);
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Réinitialiser l'échéancier
  const handleResetEcheancier = async (doc) => {
    if (!doc?.id) {
      toast.error('ID de souscription manquant');
      return;
    }

    const echeances = doc?.echeancier || [];
    const paidCount = echeances.filter(e => e.paye || e.statut === 'paye').length;

    if (paidCount === 0) {
      toast.info('Aucune échéance payée à réinitialiser');
      return;
    }

    if (!window.confirm(`Réinitialiser ${paidCount} échéance(s) payée(s) ? Les statuts seront remis à "En attente".`)) {
      return;
    }

    try {
      await axios.put(
        `${API}/souscriptions/admin/${doc.id}/reset-echeancier`,
        {},
        { headers: getAuthHeader() }
      );

      toast.success(`Échéancier réinitialisé (${echeances.length} échéances remises en attente)`);
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  // ============================================
  // FONCTIONS D'EXPORT CSV/EXCEL
  // ============================================

  // Export générique CSV
  const exportToCSV = (data, headers, filename) => {
    setExportLoading(true);
    try {
      const csvContent = [
        headers.join(';'),
        ...data.map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(';'))
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      
      toast.success(`Export CSV "${filename}" téléchargé`);
    } catch {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExportLoading(false);
    }
  };

  // Export des acomptes
  const exportAcomptes = () => {
    const headers = ['Référence', 'Souscription', 'Entreprise', 'SIRET', 'Montant', 'Date', 'Mode', 'Statut', 'Référence Paiement'];
    const data = getAllAcomptesESS
      .filter(a => {
        if (filterDateDebut && new Date(a.date) < new Date(filterDateDebut)) return false;
        if (filterDateFin && new Date(a.date) > new Date(filterDateFin)) return false;
        return true;
      })
      .map(a => [
        a.reference,
        a.docReference,
        a.docName,
        a.docSiret,
        (a.amount || a.montant || 0).toFixed(2),
        formatDate(a.date),
        a.method || a.mode_paiement || 'Virement',
        a.is_scheduled ? 'Programmé' : 'Reçu',
        a.reference || ''
      ]);
    exportToCSV(data, headers, 'acomptes_ess');
  };

  // Export des échéanciers
  const exportEcheanciers = () => {
    const headers = ['Souscription', 'Entreprise', 'Nb Échéances', 'Montant Total', 'Échéances Payées', 'Montant Payé', 'Reste à Payer', 'Prochaine Échéance'];
    const data = allDocuments
      .filter(doc => doc.echeancier && doc.echeancier.length > 0)
      .filter(doc => {
        if (filterDateDebut && new Date(doc.date_creation) < new Date(filterDateDebut)) return false;
        if (filterDateFin && new Date(doc.date_creation) > new Date(filterDateFin)) return false;
        return true;
      })
      .map(doc => {
        const echeances = doc.echeancier || [];
        const payees = echeances.filter(e => e.statut === 'paye');
        const montantPaye = payees.reduce((s, e) => s + (e.montant || 0), 0);
        const montantTotal = echeances.reduce((s, e) => s + (e.montant || 0), 0);
        const prochaine = echeances.find(e => e.statut !== 'paye');
        
        return [
          doc.numero_police,
          doc.raison_sociale,
          echeances.length,
          montantTotal.toFixed(2),
          payees.length,
          montantPaye.toFixed(2),
          (montantTotal - montantPaye).toFixed(2),
          prochaine ? formatDate(prochaine.date_prevue) : 'Terminé'
        ];
      });
    exportToCSV(data, headers, 'echeanciers_ess');
  };

  // Export des contributions acquittées
  const exportContributionsAcquittees = () => {
    const headers = ['Référence', 'Entreprise', 'SIRET', 'Montant Global', 'Date Souscription', 'Date Paiement', 'Mode Paiement'];
    const data = allDocuments
      .filter(doc => doc.paiement_valide || doc.est_solde)
      .filter(doc => {
        if (filterDateDebut && new Date(doc.date_creation) < new Date(filterDateDebut)) return false;
        if (filterDateFin && new Date(doc.date_creation) > new Date(filterDateFin)) return false;
        return true;
      })
      .map(doc => [
        doc.numero_police,
        doc.raison_sociale,
        doc.siret,
        calculateMontantTTC(doc).toFixed(2),
        formatDate(doc.date_creation),
        formatDate(doc.date_paiement || doc.date_validation),
        doc.mode_paiement || 'Virement'
      ]);
    exportToCSV(data, headers, 'contributions_acquittees');
  };

  // Export des paiements par mode
  const exportPaiementsParMode = () => {
    const headers = ['Mode', 'Nombre Paiements', 'Montant Total', 'Pourcentage'];
    const total = calculatedComptaStats.totalAcomptes || 1;
    const data = [
      ['Virement', calculatedComptaStats.virement.count, calculatedComptaStats.virement.montant.toFixed(2), ((calculatedComptaStats.virement.montant / total) * 100).toFixed(1) + '%'],
      ['SEPA Core', calculatedComptaStats.sepaCore.count, calculatedComptaStats.sepaCore.montant.toFixed(2), ((calculatedComptaStats.sepaCore.montant / total) * 100).toFixed(1) + '%'],
      ['SEPA B2B', calculatedComptaStats.sepaB2B.count, calculatedComptaStats.sepaB2B.montant.toFixed(2), ((calculatedComptaStats.sepaB2B.montant / total) * 100).toFixed(1) + '%'],
      ['CB Stripe', calculatedComptaStats.cb.count, calculatedComptaStats.cb.montant.toFixed(2), ((calculatedComptaStats.cb.montant / total) * 100).toFixed(1) + '%'],
      ['Chèque', calculatedComptaStats.cheque.count, calculatedComptaStats.cheque.montant.toFixed(2), ((calculatedComptaStats.cheque.montant / total) * 100).toFixed(1) + '%']
    ];
    exportToCSV(data, headers, 'paiements_par_mode');
  };

  // Export des paiements SEPA
  const exportPaiementsSEPA = () => {
    const headers = ['Type SEPA', 'Souscription', 'Entreprise', 'Montant', 'Date', 'Statut', 'Notification J-', 'Remboursable'];
    const data = getAllAcomptesESS
      .filter(a => {
        const mode = (a.method || a.mode_paiement || '').toLowerCase();
        return mode.includes('sepa');
      })
      .filter(a => {
        if (filterDateDebut && new Date(a.date) < new Date(filterDateDebut)) return false;
        if (filterDateFin && new Date(a.date) > new Date(filterDateFin)) return false;
        return true;
      })
      .map(a => {
        const mode = (a.method || a.mode_paiement || '').toLowerCase();
        const isB2B = mode.includes('b2b');
        return [
          isB2B ? 'SEPA B2B' : 'SEPA Core',
          a.docReference,
          a.docName,
          (a.amount || a.montant || 0).toFixed(2),
          formatDate(a.date),
          a.is_scheduled ? 'Programmé' : 'Prélevé',
          isB2B ? '1 jour' : '14 jours',
          isB2B ? 'Non' : 'Oui (8 sem)'
        ];
      });
    exportToCSV(data, headers, 'paiements_sepa');
  };

  // Export Excel complet (tous les onglets)
  const exportExcelComplet = async () => {
    setExportLoading(true);
    try {
      const response = await axios.post(
        `${API}/export/ess/excel-complet`,
        {
          date_debut: filterDateDebut || null,
          date_fin: filterDateFin || null,
          include_acomptes: true,
          include_echeanciers: true,
          include_contributions: true,
          include_sepa: true,
          include_stats: true
        },
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `export_comptable_ess_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Export Excel complet téléchargé');
    } catch {
      toast.info('Export Excel non disponible, téléchargement des CSV...');
      exportAcomptes();
      exportEcheanciers();
      exportContributionsAcquittees();
      exportPaiementsSEPA();
    } finally {
      setExportLoading(false);
    }
  };

  // ============================================
  // FONCTIONS ASSIGNATION & PARTAGE
  // ============================================

  // Fonction d'assignation
  const handleAssign = async () => {
    if (!selectedDocument || !assignForm.assign_type || !assignForm.assign_id) {
      toast.error('Veuillez sélectionner un type et une personne');
      return;
    }
    
    try {
      const docId = selectedDocument.id || selectedDocument.numero_police;
      const updateData = {};
      
      if (assignForm.assign_type === 'cooper') {
        updateData.cooper_id = assignForm.assign_id;
      } else if (assignForm.assign_type === 'member') {
        updateData.membre_id = assignForm.assign_id;
      } else if (assignForm.assign_type === 'expert') {
        updateData.expert_id = assignForm.assign_id;
      }
      
      await axios.patch(
        `${API}/souscriptions/admin/${docId}/assign`,
        updateData,
        { headers: getAuthHeader() }
      );
      
      toast.success('Assignation effectuée avec succès');
      setShowAssignModal(false);
      setAssignForm({ assign_type: '', assign_id: '' });
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    }
  };

  // Fonction de partage
  const handleShare = async () => {
    if (!selectedDocument || shareForm.recipients.length === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire');
      return;
    }
    
    setSharing(true);
    try {
      const docId = selectedDocument.id || selectedDocument.numero_police;
      
      await axios.post(
        `${API}/souscriptions/admin/${docId}/share`,
        {
          recipients: shareForm.recipients,
          method: shareForm.method,
          message: shareForm.message,
          include_recu: shareForm.include_recu
        },
        { headers: getAuthHeader() }
      );
      
      toast.success('Document partagé avec succès');
      setShowShareModal(false);
      setShareForm({ recipients: [], method: 'email', message: '', include_recu: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du partage');
    } finally {
      setSharing(false);
    }
  };

  // Ouvrir modal de partage
  const openShareModal = (doc) => {
    setSelectedDocument(doc);
    setShareForm({
      recipients: [],
      method: 'email',
      message: `Bonjour,\n\nVeuillez trouver ci-joint le reçu de paiement pour la souscription ${doc.raison_sociale || doc.numero_police}.\n\nCordialement,`,
      include_recu: true
    });
    setShowShareModal(true);
  };

  return {
    printEcheancier,
    handleDeleteEcheancier,
    handleResetEcheancier,
    exportToCSV,
    exportAcomptes,
    exportEcheanciers,
    exportContributionsAcquittees,
    exportPaiementsParMode,
    exportPaiementsSEPA,
    exportExcelComplet,
    handleAssign,
    handleShare,
    openShareModal,
  };
}
